/**
 * S7 · Review your order. NO payment SDK on this screen (N2).
 *
 * The Stripe Payment Element used to sit under a long review summary, which made
 * this the longest screen in the funnel and put the card form below the fold.
 * The split is:
 *   /create/pay        → THIS screen: what you're ordering, what it costs.
 *   /create/authorise  → the payment screen (S7bAuthorise), which owns Stripe
 *                        and owns the submit.
 * There is still ONE submit path and ONE renderer for the card; nothing here is
 * duplicated on the payment screen.
 *
 * MONEY BOUNDARY: the total is derived from the card fee ALONE.
 * `intended_load_amount_cents` is shown below the total, never added to it.
 *
 * LEGAL GATES (L1): no scheme mark/name; prices are the configured programme
 * values only.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { readDetails, readSelectedVariant, readStylePick, saveDetails } from "@/lib/consumer-session";
import { findTile } from "@/lib/style-art";
import { useConsumerFlow } from "../ConsumerFlow";
import { postalAddressSummary, useRepeatProfile } from "@/lib/repeat-flow";
import {
  Display,
  FunnelContainer,
  FunnelHeader,
  FunnelPage,
  Pill,
  PrimaryButton,
  SectionTitle,
  StickyBar,
} from "@/components/consumer/chrome";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S7_SECTION, S7_DEFAULTS } from "@/content/screens/s7";
import {
  formatMoney,
  normaliseQuantity,
  resolveOrderPrice,
  resolvePricing,
  resolveQuantityOptions,
} from "@/lib/consumer-config";
import { DesignedCardFace } from "@/components/consumer/DesignedCardFace";
import { readDesignedCard } from "@/lib/designed-card";
import { cn } from "@/lib/utils";

export default function S7Pay() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const repeat = useRepeatProfile(program.slug);
  const { t } = useSiteContent(S7_SECTION, S7_DEFAULTS);

  const variant = readSelectedVariant(program.slug);
  const designedCard = readDesignedCard(program.slug);
  const details = readDetails(program.slug);
  const pick = readStylePick(program.slug);
  const styleName = findTile(program.styles, pick?.style_id)?.name || pick?.style_id || "Your card";
  const cfg = (program.config ?? {}) as Record<string, any>;

  // DISPLAY-ONLY. resolvePricing() reads cardFeeCents/pricingTiers/currency and
  // never touches fundingAmountsCents, so no load can leak into a Cardmoji total.
  const pricing = resolvePricing(cfg, program.currency);
  /* HOW MANY CARDS. The chooser offers CONFIGURED tiers only (see
     resolveQuantityOptions) and the price is the tier's price — never the single
     fee multiplied. Three cards therefore cost the configured three-card price,
     which is the defect this replaces. */
  const quantityOptions = resolveQuantityOptions(pricing);
  const [quantity, setQuantity] = useState(() =>
    normaliseQuantity(pricing, readDetails(program.slug)?.card_quantity ?? 1),
  );
  const orderPrice = resolveOrderPrice(pricing, quantity);
  const total = orderPrice.cents; // fee ONLY — intentionally not `+ load`.
  const intendedLoadCents = details?.intended_load_amount_cents ?? null;

  useEffect(() => {
    if (!variant || !details) navigate("../name", { replace: true });
  }, [variant, details, navigate]);

  /* The chosen quantity is staged on THIS order's draft. The server re-resolves
     the amount from configuration and refuses a quantity the authorisation was
     not created for, so this is a selection, never a price assertion. */
  function chooseQuantity(next: number) {
    setQuantity(next);
    const current = readDetails(program.slug);
    if (current) saveDetails(program.slug, { ...current, card_quantity: next } as any);
  }

  if (!variant || !details) return null;

  const deliveryLine =
    details.delivery_type === "in_branch"
      ? `${t("s7.delivery.collectPrefix")}${details.branch_id || t("s7.delivery.collectFallback")}`
      : `${t("s7.delivery.mailPrefix")}${details.postal_address?.postcode ?? t("s7.delivery.mailFallback")}`;

  /** Full read-only address through the ONE shared summariser. */
  const addressLine =
    postalAddressSummary(
      details.postal_address as Record<string, string> | null | undefined,
      details.delivery_type,
    ) ?? deliveryLine;

  return (
    <FunnelPage testId="s7-pay">
      <FunnelContainer size="form">
        <FunnelHeader
          backTo="../load"
          backLabel={t("s7.header.backLabel")}
          step={repeat.isRepeat ? t("s7.header.stepRepeat") : t("s7.header.stepFirst")}
        />

        {/* U4 · one H1 and no lede. The hold/authorise explanation lives on the
            payment screen, where it is actually relevant. */}
        <Display className="mb-[22px] max-w-[20ch]">{t("s7.order.title")}</Display>

        <div className="mb-[22px] rounded-[18px] border border-foreground/10 bg-white p-[18px]">
          <div className="mb-3 flex items-center gap-3.5">
            {/* ONE renderer + THE designed card. U1 · no "Change" control beside
                it — the header back arrow is the single way back. */}
            <div className={cn("flex-none", designedCard.orientation === "landscape" ? "w-[88px]" : "w-14")}>
              <DesignedCardFace
                card={designedCard}
                alt={styleName}
                readOnly
                data-testid="s7-card"
                className="rounded-lg"
              />
            </div>
            <div className="flex min-w-0 flex-col gap-[3px]">
              <span className="text-[16px] font-semibold">{styleName}</span>
              <span className="text-[13px] text-foreground/70" data-testid="s7-detail-name">
                {details.first_name} {details.last_name}
              </span>
              <span className="text-[13px] text-foreground/70" data-testid="s7-detail-delivery">
                {addressLine}
              </span>
            </div>
          </div>

          {details.pan_last5 ? (
            <div className="flex items-start justify-between gap-4 border-t border-foreground/10 pt-3">
              <span className="text-[13px] uppercase tracking-[0.06em] text-foreground/55">Card number</span>
              <span className="text-[14px] font-semibold" data-testid="s7-detail-pan">
                •••• {details.pan_last5}
              </span>
            </div>
          ) : null}

          {/* HOW MANY CARDS — shown only when more than one tier is configured. */}
          {quantityOptions.length > 1 && (
            <fieldset className="border-t border-foreground/10 pt-3" data-testid="s7-quantity">
              <legend className="mb-2 text-[14px] font-semibold">{t("s7.order.quantityLabel")}</legend>
              <div className="flex flex-wrap gap-2">
                {quantityOptions.map((opt) => (
                  <Pill
                    key={opt.quantity}
                    type="button"
                    active={quantity === opt.quantity}
                    onClick={() => chooseQuantity(opt.quantity)}
                    data-testid={`s7-quantity-${opt.quantity}`}
                  >
                    {opt.label} — {formatMoney(opt.cents, pricing.currency)}
                  </Pill>
                ))}
              </div>
            </fieldset>
          )}

          {/* Cardmoji's charge for THIS order. One fee line, one total. */}
          <div className="border-t border-foreground/10 pt-3">
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-[14px] text-foreground/70">{t("s7.order.feeLabel")}</span>
              <span className="text-[15px] font-semibold" data-testid="s7-fee">
                {orderPrice.cents !== null
                  ? formatMoney(orderPrice.cents, pricing.currency)
                  : t("s7.order.feeUnavailable")}
              </span>
            </div>

            {total !== null && (
              <div className="mt-3 flex items-baseline justify-between gap-4 border-t border-foreground/10 pt-3">
                <span className="text-[14px] font-semibold">{t("s7.order.totalLabel")}</span>
                <span className="text-[18px] font-semibold" data-testid="s7-total">
                  {formatMoney(total, pricing.currency)}
                </span>
              </div>
            )}
          </div>

          {/* The BANK's load. Rendered BELOW the total and never added to it. */}
          {intendedLoadCents !== null && (
            <div className="mt-3 rounded-[14px] bg-foreground/[0.04] p-3" data-testid="s7-intended-load">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[14px] text-foreground/70">{t("s7.order.loadLabel")}</span>
                <span className="text-[15px] font-semibold" data-testid="s7-intended-load-amount">
                  {formatMoney(intendedLoadCents, pricing.currency)}
                </span>
              </div>
              <p className="mt-1.5 text-[13px] leading-[1.5] text-foreground/60">{t("s7.order.loadNote")}</p>
            </div>
          )}
        </div>

        <SectionTitle className="sr-only">{t("s7.order.title")}</SectionTitle>
      </FunnelContainer>

      <StickyBar>
        <PrimaryButton
          onClick={() => navigate("../authorise")}
          data-testid="s7-continue"
          className="basis-full"
        >
          {t("s7.footer.reviewContinue")}
        </PrimaryButton>
      </StickyBar>
    </FunnelPage>
  );
}
